import { Download, DownloadCloud } from "lucide-react";

import { Button } from "@/components/ui/button";
import type { SocialAdRender, SocialAdSet } from "@/lib/api";

import { AdCopyPreview } from "./social-ad-copy";
import { LANGUAGE_NAMES } from "./utils";

interface SocialAdResultsProps {
  ads: SocialAdSet;
}

/**
 * The rendered ads, as a contact sheet of what would actually be posted.
 *
 * Every format is a different shape — a square next to a story is 1:1 next to
 * 9:16 — so each preview keeps its own proportions rather than being forced
 * into a uniform tile. Cropping them to a grid would hide the one thing worth
 * checking: whether the copy still sits where it should once the frame changes.
 */
export function SocialAdResults({ ads }: SocialAdResultsProps) {
  const renders = ads.renders ?? [];
  const language = LANGUAGE_NAMES[ads.language] ?? ads.language;

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-muted text-xs">
          <span className="text-foreground font-semibold">{renders.length}</span> ad
          {renders.length === 1 ? "" : "s"} rendered in {language} · same photo, same copy, one file
          per format
        </p>
        {renders.length > 1 && (
          <Button variant="outline" size="sm" onClick={() => downloadAll(renders)}>
            <DownloadCloud className="mr-1.5 h-3.5 w-3.5" />
            Download all {renders.length}
          </Button>
        )}
      </div>

      <ul className="grid grid-cols-2 items-start gap-3 sm:grid-cols-3">
        {renders.map((render) => (
          <li
            key={render.format_key}
            className="border-border bg-card space-y-2 rounded-lg border p-2"
          >
            <div className="bg-surface-muted flex items-center justify-center rounded-md p-1">
              <img
                src={render.preview_url}
                alt={`${render.label} ad, ${render.width} by ${render.height}`}
                loading="lazy"
                // The ratio comes from the render itself, so the box is the
                // right shape before the image has loaded and nothing on the
                // page jumps when it does. `object-contain` is the guarantee
                // that a narrow column can never squash an ad.
                style={{ aspectRatio: `${render.width} / ${render.height}` }}
                className="max-h-72 w-full rounded-sm object-contain"
              />
            </div>
            <div className="min-w-0 space-y-1 px-0.5">
              <p className="text-foreground truncate text-xs font-semibold">{render.label}</p>
              <p className="text-muted text-[11px] tabular-nums">
                {render.width} × {render.height}
              </p>
              <Button asChild variant="ghost" size="sm" className="-ml-2 h-7 px-2">
                <a href={render.download_url} download>
                  <Download className="mr-1.5 h-3 w-3" />
                  Download
                </a>
              </Button>
            </div>
          </li>
        ))}
      </ul>

      <AdCopyPreview copy={ads.copy} heading="The copy on these ads" />

      {/* The conversation does not end at a file: the next round is another ask,
          same as the deck flow, and each one lands in the version list beside
          the thread. */}
      <p className="text-muted text-xs leading-5">
        Not quite right? Ask below — shorter copy, another language, a different photo — and
        I&apos;ll render a new version of this ad.
      </p>
    </section>
  );
}

/**
 * Hand over every format at once.
 *
 * The backend does bundle these into a zip — that zip is the artifact
 * registered on the turn, and it is still one click away in the file's element
 * view. Here the ads are posted one per format, so handing over the files
 * themselves saves unpacking an archive to get at them. The downloads are
 * spaced apart because browsers treat a burst of clicks from one gesture as
 * something to block.
 */
function downloadAll(renders: SocialAdRender[]): void {
  renders.forEach((render, index) => {
    window.setTimeout(() => {
      const link = document.createElement("a");
      link.href = render.download_url;
      link.download = "";
      document.body.append(link);
      link.click();
      link.remove();
    }, index * 300);
  });
}
